import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { clarifyTicketAgeCopy } from '../../utils/clarifyTicketAgeLabel';

const logoStacked = 'https://BelvoirCastle.b-cdn.net/Logos/logo_stacked.webp';

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4 portrait;
      margin: 12mm 14mm;
    }
    body {
      margin: 0;
      padding: 0;
      background: white !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  }
`;

/** Normalize "10:00", "10:00:00", etc. to HH:MM for comparison and display */
function normalizeHm(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (s === 'Not specified') return null;
  const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  h = Math.min(Math.max(h, 0), 23);
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Best-effort start time for this line item (tours, tea, upsells, etc.) */
function extractProductStartTime(ticket) {
  if (!ticket || typeof ticket !== 'object') return null;
  if (ticket.startTime) {
    const n = normalizeHm(ticket.startTime);
    if (n) return n;
  }
  if (ticket.selectedEventTime?.time) {
    const n = normalizeHm(ticket.selectedEventTime.time);
    if (n) return n;
  }
  if (ticket.selectedEventTime?.startDateTime) {
    const d = new Date(ticket.selectedEventTime.startDateTime);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
  }
  if (ticket.selectedDate && String(ticket.selectedDate).includes('T')) {
    const tail = String(ticket.selectedDate).split('T')[1];
    if (tail) return normalizeHm(tail);
  }
  return null;
}

function productIsScheduledAddon(ticket) {
  const n = (ticket?.name || ticket?.title || '').toLowerCase();
  const keywords = [
    'tour',
    'afternoon tea',
    'afternoon',
    ' tea',
    'tea ',
    'grotto',
    'experience',
    'sitting',
    'crown',
    'butler',
    'workshop',
    'dining',
    'flower',
    'garden show',
    'safari'
  ];
  if (keywords.some(k => n.includes(k))) return true;
  const se = ticket?.selectedEventTime;
  if (se && typeof se === 'object' && (se.time || se.startDateTime)) return true;
  return Boolean(ticket?.startTime);
}

/** Time to print under the product name */
function getPrintedStartTime(ticket, entryHm) {
  const extracted = extractProductStartTime(ticket);
  if (productIsScheduledAddon(ticket)) {
    return extracted || entryHm || null;
  }
  if (extracted && entryHm && extracted !== entryHm) return extracted;
  if (extracted && !entryHm) return extracted;
  return null;
}

/** Visit falls within Belvoir Summer Safari 2026 (13 Jul – 1 Sep) */
function isDateInSafariPeriod(date) {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  const start = new Date('2026-07-13T00:00:00');
  const end = new Date('2026-09-01T23:59:59');
  return d >= start && d <= end;
}

/** Ticket names suggest a safari-season visit (garden/castle entry or free animal park promo) */
function ticketNamesSuggestSafariSeason(names) {
  return names.some(n =>
    n.includes('safari') ||
    (n.includes('animal park') && n.includes('promotion')) ||
    n.includes('garden') ||
    n.includes('castle')
  );
}

/** Ticket line indicates a child admission/product */
function bookingHasChildTicket(tickets) {
  return (tickets || []).some(t => {
    if (!(t.quantity > 0)) return false;
    const n = (t.name || t.title || '').toLowerCase();
    return n.includes('child') || n.includes('junior') || n.includes('under ');
  });
}

/* ─── Shell ────────────────────────────────────────────────────────────── */

const Wrap = styled.div`
  width: 100%;
  max-width: 210mm;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

  @media print {
    max-width: 100%;
    margin: 0;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #d8d4cd;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(16, 29, 64, 0.10);

  @media print {
    border: 1px solid #c8c4bc;
    box-shadow: none;
    border-radius: 0;
  }
`;

/* ─── Top accent bar ───────────────────────────────────────────────────── */

const AccentBar = styled.div`
  height: 4px;
  background: ${props => props.$safari
    ? 'linear-gradient(90deg, #C8861A 0%, #A83E18 100%)'
    : 'linear-gradient(90deg, #9d6d49 0%, #101d40 100%)'};
  background-color: ${props => props.$safari ? '#C8861A' : '#9d6d49'};
`;

/* ─── Header ───────────────────────────────────────────────────────────── */

const Header = styled.div`
  background: #101d40;
  padding: 18px 24px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media print {
    padding: 14px 20px 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Logo = styled.img`
  height: 42px;
  width: auto;
  display: block;
  filter: brightness(0) invert(1);

  @media print {
    height: 34px;
  }
`;

const CastleLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const CastleName = styled.span`
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ffffff;
  font-family: Georgia, 'Times New Roman', serif;
  line-height: 1.1;

  @media print {
    font-size: 1rem;
  }
`;

const CastleSub = styled.span`
  font-size: 0.6875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 3px;

  @media print {
    font-size: 0.625rem;
  }
`;

const TicketBadge = styled.div`
  padding: 5px 12px;
  border: 1px solid rgba(157, 109, 73, 0.7);
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.75);

  @media print {
    font-size: 0.625rem;
    padding: 4px 10px;
  }
`;

/* ─── Body ─────────────────────────────────────────────────────────────── */

const Body = styled.div`
  padding: 20px 24px 0;

  @media print {
    padding: 14px 20px 0;
  }
`;

/* ─── Top row: details + QR ────────────────────────────────────────────── */

const TopRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 16px;

  @media print {
    gap: 16px;
    margin-bottom: 12px;
  }
`;

const DetailsCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const QRCol = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

/* ─── Reference ────────────────────────────────────────────────────────── */

const RefBlock = styled.div`
  background: #f8f7f4;
  border: 1px solid #e8e4dc;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;

  @media print {
    padding: 8px 12px;
    margin-bottom: 9px;
  }
`;

const RefLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  white-space: nowrap;

  @media print {
    font-size: 0.625rem;
  }
`;

const RefCode = styled.span`
  font-family: ui-monospace, 'SF Mono', Consolas, monospace;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #101d40;

  @media print {
    font-size: 0.875rem;
  }
`;

/* ─── Detail rows ──────────────────────────────────────────────────────── */

const DetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e8e4dc;
  border-radius: 8px;
  overflow: hidden;

  @media print {
    border-color: #d8d4cc;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 9px 14px;
  background: #ffffff;
  border-bottom: 1px solid #f0ede7;

  &:last-child {
    border-bottom: none;
  }

  @media print {
    padding: 7px 12px;
    border-bottom-color: #ebe8e1;
  }
`;

const DLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(16, 29, 64, 0.48);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  flex-shrink: 0;

  @media print {
    font-size: 0.6875rem;
  }
`;

const DValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #101d40;
  text-align: right;

  @media print {
    font-size: 0.8125rem;
  }
`;

const DValueAccent = styled(DValue)`
  color: #9d6d49;
  font-size: 1rem;

  @media print {
    font-size: 0.9375rem;
  }
`;

/* ─── QR code ──────────────────────────────────────────────────────────── */

const QRBox = styled.div`
  width: 110px;
  height: 110px;
  padding: 6px;
  border: 1px solid #d8d4cc;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
  }

  @media print {
    width: 96px;
    height: 96px;
    padding: 5px;
  }
`;

const QRCaption = styled.span`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  text-align: center;
  max-width: 110px;
  line-height: 1.35;

  @media print {
    font-size: 0.5625rem;
    max-width: 96px;
  }
`;

/* ─── Divider ──────────────────────────────────────────────────────────── */

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ebe8e1;
  margin: 14px 0;

  @media print {
    margin: 10px 0;
  }
`;

/* ─── Ticket lines ─────────────────────────────────────────────────────── */

const TicketSection = styled.div`
  margin-bottom: 14px;

  @media print {
    margin-bottom: 10px;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  margin-bottom: 7px;

  @media print {
    font-size: 0.625rem;
    margin-bottom: 5px;
  }
`;

const ProductCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
`;

const NameRow = styled.span`
  display: block;
`;

const TicketLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 7px 12px;
  background: ${props => props.highlight ? '#f8f7f4' : '#ffffff'};
  border-radius: 6px;
  margin-bottom: 3px;
  font-size: 0.875rem;
  color: #101d40;

  &:last-child {
    margin-bottom: 0;
  }

  @media print {
    padding: 5px 10px;
    font-size: 0.8125rem;
    margin-bottom: 2px;
  }
`;

const TName = styled.span`
  font-weight: 600;
  color: #101d40;
`;

const TQty = styled.span`
  font-weight: 400;
  color: rgba(16, 29, 64, 0.5);
  font-size: 0.8125rem;
  margin-left: 4px;
`;

const TTime = styled.span`
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #9d6d49;

  @media print {
    font-size: 0.75rem;
  }
`;

const TPrice = styled.span`
  font-weight: 600;
  color: #9d6d49;
  white-space: nowrap;
`;

const LocationNote = styled.div`
  font-size: 0.75rem;
  color: rgba(16, 29, 64, 0.5);
  font-style: italic;
  padding: 3px 12px 5px 22px;

  @media print {
    font-size: 0.6875rem;
  }
`;

/* ─── Savings strip ────────────────────────────────────────────────────── */

const SavingsStrip = styled.div`
  background: #f8f7f4;
  border: 1px solid #e8e4dc;
  border-left: 3px solid #9d6d49;
  border-radius: 6px;
  padding: 9px 14px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  @media print {
    padding: 7px 12px;
    margin-bottom: 10px;
  }
`;

const SavingsLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SavingsPill = styled.span`
  display: inline-block;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.6);
  background: rgba(157, 109, 73, 0.12);
  padding: 2px 7px;
  border-radius: 999px;
  width: fit-content;
`;

const SavingsDesc = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #101d40;

  @media print {
    font-size: 0.75rem;
  }
`;

const SavingsAmt = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #9d6d49;
  white-space: nowrap;

  @media print {
    font-size: 1rem;
  }
`;

/* ─── Safari strip ─────────────────────────────────────────────────────── */

const SafariStrip = styled.div`
  background: linear-gradient(135deg, #fff8ee 0%, #fff3e0 100%);
  border: 1px solid #f0c070;
  border-left: 3px solid #C8861A;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 14px;

  @media print {
    padding: 9px 12px;
    margin-bottom: 10px;
  }
`;

const SafariStripLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #C8861A;
  margin-bottom: 8px;

  @media print {
    font-size: 0.625rem;
    margin-bottom: 6px;
  }
`;

const SafariIncludes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px 16px;
`;

const SafariItem = styled.span`
  font-size: 0.8125rem;
  color: #101d40;
  display: flex;
  align-items: center;
  gap: 5px;

  @media print {
    font-size: 0.75rem;
  }
`;

const IceCreamNote = styled.div`
  margin-top: 10px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #A83E18;
  line-height: 1.45;

  @media print {
    font-size: 0.75rem;
    margin-top: 8px;
  }
`;

/* ─── Info bar ─────────────────────────────────────────────────────────── */

const InfoBar = styled.div`
  background: #f8f7f4;
  border-top: 1px solid #ebe8e1;
  padding: 11px 24px;
  font-size: 0.75rem;
  color: rgba(16, 29, 64, 0.55);
  line-height: 1.5;

  strong {
    color: #101d40;
    font-weight: 600;
  }

  @media print {
    padding: 9px 20px;
    font-size: 0.6875rem;
  }
`;

/* ─── Footer ───────────────────────────────────────────────────────────── */

const FooterBar = styled.div`
  background: #101d40;
  padding: 13px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media print {
    padding: 10px 20px;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FooterBrand = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: #ffffff;
  font-family: Georgia, 'Times New Roman', serif;
  letter-spacing: 0.05em;

  @media print {
    font-size: 0.8125rem;
  }
`;

const FooterAddress = styled.span`
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.5);

  @media print {
    font-size: 0.625rem;
  }
`;

const FooterRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const FooterLink = styled.span`
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.6);

  @media print {
    font-size: 0.625rem;
  }
`;

/* ─── Component ────────────────────────────────────────────────────────── */

const PrintableTicket = ({
  bookingReference,
  selectedTickets,
  selectedDate,
  selectedTimeSlot,
  customerDetails,
  totalAmount,
  venposBasket,
  bookingSummary,
  isFamilySaver = false
}) => {
  const lowerTicketNames = (selectedTickets || []).map(t => (t.name || t.title || '').toLowerCase());
  const pageId = venposBasket?.data?.pageId || bookingSummary?.pageId || null;
  const isFlowerGardenShow =
    pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09' ||
    lowerTicketNames.some(n => n.includes('flower') && n.includes('garden'));

  const SAFARI_CASTLE_PAGE_ID  = '94fbd17d-c40c-49f3-878e-d0bc415926b2';
  const SAFARI_GARDENS_PAGE_ID = '6d614bac-7055-4932-b62b-c960e7ff213b';
  const isSafariByPage   = pageId === SAFARI_CASTLE_PAGE_ID || pageId === SAFARI_GARDENS_PAGE_ID;
  const isSafariBySeason = isDateInSafariPeriod(selectedDate) && ticketNamesSuggestSafariSeason(lowerTicketNames);
  const isSafari         = isSafariByPage || isSafariBySeason;
  const isSafariCastle   = pageId === SAFARI_CASTLE_PAGE_ID
    || (isSafariBySeason && !isSafariByPage && lowerTicketNames.some(n => n.includes('castle') && !n.includes('garden')));
  const hasChildTicket   = bookingHasChildTicket(selectedTickets);

  const formatDate = (date) =>
    date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const formatTime = (timeSlot) => timeSlot?.time || timeSlot || 'Not specified';

  if (!selectedTickets || !selectedDate || !customerDetails) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ color: '#101d40' }}>Unable to load ticket</h2>
        <p>Required booking data is missing. Please return to your confirmation page.</p>
      </div>
    );
  }

  const entryTime = formatTime(selectedTimeSlot);
  const entryHm =
    normalizeHm(selectedTimeSlot?.time) ||
    (typeof selectedTimeSlot === 'string' ? normalizeHm(selectedTimeSlot) : null);

  const addonTimes = [...new Set(
    selectedTickets
      .filter(t => t.quantity > 0)
      .map(t => extractProductStartTime(t))
      .filter(t => t && (!entryHm || t !== entryHm))
  )];

  const totalTickets = selectedTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);
  const basketData = venposBasket?.data;
  const discounts = basketData?.discounts || bookingSummary?.discounts || [];
  const totalSavings = bookingSummary?.totalSavings || discounts.reduce((sum, d) => sum + (d.discountedAmount || 0), 0);
  const hasSavings = totalSavings > 0;

  const isFamilySaverDiscount = discounts.some(d => d.code && (d.code.toUpperCase().startsWith('FS') || d.code.toUpperCase().startsWith('FAMILYSAVER')));
  const isTravelZooDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('TZ'));

  let originalPrice, finalPrice;
  if (bookingSummary?.originalPrice && bookingSummary?.finalPrice !== undefined) {
    originalPrice = bookingSummary.originalPrice;
    finalPrice = bookingSummary.finalPrice;
  } else if (basketData?.totalPrice && basketData?.finalPrice !== undefined) {
    originalPrice = basketData.totalPrice;
    finalPrice = basketData.finalPrice;
  } else {
    originalPrice = totalAmount;
    finalPrice = totalAmount;
  }

  const savingsPillText = isFamilySaverDiscount
    ? 'Family Saver'
    : isTravelZooDiscount
      ? 'Travelzoo'
      : 'Promotional offer';

  return (
    <>
      <PrintStyles />
      <Wrap>
        <Card className="printable-ticket">
          <AccentBar $safari={isSafari} />
          <Header>
            <HeaderLeft>
              <Logo src={logoStacked} alt="Belvoir Castle" />
              <CastleLabel>
                <CastleName>Belvoir Castle</CastleName>
                <CastleSub>{isSafari ? 'Summer Safari 2026' : 'Leicestershire'}</CastleSub>
              </CastleLabel>
            </HeaderLeft>
            <TicketBadge>{isSafari ? (isSafariCastle ? 'Castle Entry' : 'Gardens Entry') : 'Admission Ticket'}</TicketBadge>
          </Header>

          <Body>
            <TopRow>
              {/* Left: booking details */}
              <DetailsCol>
                <RefBlock>
                  <RefLabel>Booking reference</RefLabel>
                  <RefCode>{bookingReference}</RefCode>
                </RefBlock>

                <DetailGrid>
                  <DetailRow>
                    <DLabel>Visit date</DLabel>
                    <DValue>{formatDate(selectedDate)}</DValue>
                  </DetailRow>
                  <DetailRow>
                    <DLabel>Entry time</DLabel>
                    <DValue>{entryTime}</DValue>
                  </DetailRow>
                  {addonTimes.map(t => (
                    <DetailRow key={t}>
                      <DLabel>Add-on time</DLabel>
                      <DValue>{t}</DValue>
                    </DetailRow>
                  ))}
                  <DetailRow>
                    <DLabel>Guest</DLabel>
                    <DValue>{customerDetails.firstName} {customerDetails.lastName}</DValue>
                  </DetailRow>
                  <DetailRow>
                    <DLabel>Guests</DLabel>
                    <DValue>{totalTickets} {totalTickets === 1 ? 'person' : 'people'}</DValue>
                  </DetailRow>
                  <DetailRow>
                    <DLabel>Total paid</DLabel>
                    <DValueAccent>£{(finalPrice ?? totalAmount ?? 0).toFixed(2)}</DValueAccent>
                  </DetailRow>
                </DetailGrid>
              </DetailsCol>

              {/* Right: QR code */}
              <QRCol>
                <QRBox>
                  <QRCodeSVG
                    value={bookingReference}
                    size={96}
                    level="H"
                    includeMargin={false}
                  />
                </QRBox>
                <QRCaption>Present at{'\n'}visitor hub</QRCaption>
              </QRCol>
            </TopRow>

            {/* Savings strip */}
            {hasSavings && (
              <SavingsStrip>
                <SavingsLeft>
                  <SavingsPill>{savingsPillText}</SavingsPill>
                  <SavingsDesc>Discount applied to this booking</SavingsDesc>
                </SavingsLeft>
                <SavingsAmt>−£{totalSavings.toFixed(2)} saved</SavingsAmt>
              </SavingsStrip>
            )}

            <Divider />

            {/* Ticket lines */}
            <TicketSection>
              <SectionLabel>Tickets</SectionLabel>
              {selectedTickets.filter(t => t.quantity > 0).map((ticket, index) => {
                const ticketPrice = ticket.totalPrice || (ticket.price * ticket.quantity) || 0;
                const ticketName = ticket.title || ticket.name || 'Ticket';
                const isGrotto =
                  ticketName.toLowerCase().includes("santa's grotto") &&
                  ticketName.toLowerCase().includes('retail village');
                const isAnimalParkFree =
                  ticketName.toLowerCase().includes('animal park') &&
                  ticketName.toLowerCase().includes('promotion');
                const printedStart = getPrintedStartTime(ticket, entryHm);
                return (
                  <React.Fragment key={ticket.webSaleItemId || ticket.id || index}>
                    <TicketLine>
                      <ProductCol>
                        <NameRow>
                          <TName>{clarifyTicketAgeCopy(ticketName)}</TName>
                          <TQty>×{ticket.quantity}</TQty>
                        </NameRow>
                        {printedStart && <TTime>Start time {printedStart}</TTime>}
                      </ProductCol>
                      <TPrice>£{ticketPrice.toFixed(2)}</TPrice>
                    </TicketLine>
                    {isGrotto && (
                      <LocationNote>Retail Village Santa's Grotto — approx. 5 min walk from the castle.</LocationNote>
                    )}
                    {isAnimalParkFree && (
                      <LocationNote>Free Animal Park entry included — present at Animal Park entrance.</LocationNote>
                    )}
                  </React.Fragment>
                );
              })}
            </TicketSection>

            {isSafari && (
              <SafariStrip>
                <SafariStripLabel>What's included</SafariStripLabel>
                <SafariIncludes>
                  {isSafariCastle && <SafariItem>Historic State Rooms tour</SafariItem>}
                  {isSafariCastle && <SafariItem>Bertie the Bear trail</SafariItem>}
                  <SafariItem>Formal gardens &amp; grounds</SafariItem>
                  {!isSafariCastle && <SafariItem>Adventure Playground</SafariItem>}
                  <SafariItem>Safari adventure</SafariItem>
                  <SafariItem>Free Animal Park access</SafariItem>
                </SafariIncludes>
                {hasChildTicket && (
                  <IceCreamNote>
                    Children only — each child who completes the trail can collect a voucher for one free ice cream at the end of the trail. Redeem at the Farm Shop.
                  </IceCreamNote>
                )}
              </SafariStrip>
            )}
          </Body>

          <InfoBar>
            <strong>Before your visit:</strong>&nbsp; Arrive at the time shown above &bull; Parking £4 on site &bull;
            Non-refundable &bull; Valid for the date and time shown &bull; Children must be accompanied by adults &bull;
            Contact: <strong>info@belvoircastle.com</strong> &nbsp;|&nbsp; <strong>01476 871001</strong>
            {customerDetails.email && <>&nbsp;&bull; Confirmation sent to {customerDetails.email}</>}
          </InfoBar>

          <FooterBar>
            <FooterLeft>
              <FooterBrand>Belvoir Castle</FooterBrand>
              <FooterAddress>Leicestershire, NG32 1PE</FooterAddress>
            </FooterLeft>
            <FooterRight>
              <FooterLink>www.belvoircastle.com</FooterLink>
              <FooterLink>01476 871001</FooterLink>
            </FooterRight>
          </FooterBar>
        </Card>
      </Wrap>
    </>
  );
};

export default PrintableTicket;
